import React, { useRef } from 'react';

/**
 * MintekPdfExport — Wraps content with Mintek letterhead branding and 
 * generates PDF via browser print dialog.
 * 
 * Usage:
 *   import { useMintekPdf } from '../utils/MintekPdfExport';
 *   
 *   const { printRef, triggerPrint } = useMintekPdf();
 *   
 *   <button onClick={triggerPrint}>Export PDF</button>
 *   <MintekPdfWrapper ref={printRef} title="APP Quarterly Report" subtitle="Q1 FY2025/26">
 *     <YourContent />
 *   </MintekPdfWrapper>
 */

const MINTEK_RED = '#c8102e';

// Hook for triggering PDF print
export function useMintekPdf() {
  const printRef = useRef(null);

  const triggerPrint = () => {
    if (!printRef.current) return;
    
    const printContent = printRef.current.innerHTML;
    const printWindow = window.open('', '_blank', 'width=900,height=700');
    if (!printWindow) { alert('Please allow popups for PDF export'); return; }
    
    printWindow.document.write(`<!DOCTYPE html><html><head><title>SiloGate Report</title>
      <style>
        @page { size: A4; margin: 15mm 20mm 20mm 20mm; }
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; color: #1e293b; font-size: 11px; line-height: 1.5; }
        
        .letterhead { border-bottom: 3px solid ${MINTEK_RED}; padding-bottom: 12px; margin-bottom: 20px; display: flex; align-items: center; justify-content: space-between; }
        .letterhead-logo { display: flex; align-items: center; gap: 12px; }
        .letterhead-logo .logo-box { width: 48px; height: 48px; background: ${MINTEK_RED}; border-radius: 8px; display: flex; align-items: center; justify-content: center; color: white; font-weight: 900; font-size: 22px; }
        .letterhead-logo .org-name { font-size: 20px; font-weight: 800; color: ${MINTEK_RED}; letter-spacing: -0.5px; }
        .letterhead-logo .org-sub { font-size: 9px; color: #64748b; text-transform: uppercase; letter-spacing: 2px; }
        .letterhead-right { text-align: right; font-size: 8px; color: #94a3b8; line-height: 1.6; }
        
        .report-title { text-align: center; margin: 20px 0 24px; }
        .report-title h1 { font-size: 18px; font-weight: 800; color: #0f172a; }
        .report-title p { font-size: 10px; color: #64748b; margin-top: 4px; }
        
        .footer { position: fixed; bottom: 0; left: 0; right: 0; text-align: center; font-size: 7px; color: #94a3b8; border-top: 1px solid #e2e8f0; padding-top: 6px; }
        
        table { width: 100%; border-collapse: collapse; margin: 8px 0; font-size: 10px; }
        th { background: #f1f5f9; font-weight: 700; text-transform: uppercase; font-size: 8px; letter-spacing: 0.5px; color: #64748b; padding: 6px 8px; text-align: left; border-bottom: 2px solid #e2e8f0; }
        td { padding: 5px 8px; border-bottom: 1px solid #f1f5f9; }
        
        .stat-card { display: inline-block; padding: 10px 16px; border: 1px solid #e2e8f0; border-radius: 8px; margin: 4px; text-align: center; min-width: 100px; border-left: 3px solid ${MINTEK_RED}; }
        .stat-card .label { font-size: 8px; color: #64748b; text-transform: uppercase; font-weight: 600; }
        .stat-card .value { font-size: 18px; font-weight: 900; color: #0f172a; margin-top: 2px; }
        
        .section-title { font-size: 12px; font-weight: 700; color: ${MINTEK_RED}; text-transform: uppercase; letter-spacing: 0.5px; margin: 16px 0 8px; padding-bottom: 4px; border-bottom: 1px solid #fecaca; }
        
        .badge { display: inline-block; padding: 2px 6px; border-radius: 4px; font-size: 8px; font-weight: 600; }
        .badge-green { background: #dcfce7; color: #166534; }
        .badge-red { background: #fee2e2; color: #991b1b; }
        .badge-amber { background: #fef3c7; color: #92400e; }
        .badge-blue { background: #dbeafe; color: #1e40af; }
        
        .progress-bar { height: 6px; background: #f1f5f9; border-radius: 3px; overflow: hidden; }
        .progress-fill { height: 100%; border-radius: 3px; }

        @media print { 
          body { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
          .no-print { display: none !important; }
        }
      </style>
    </head><body>
      <div class="letterhead">
        <div class="letterhead-logo">
          <div class="logo-box">M</div>
          <div>
            <div class="org-name">MINTEK</div>
            <div class="org-sub">Council for Mineral Technology</div>
          </div>
        </div>
        <div class="letterhead-right">
          200 Malibongwe Drive, Randburg 2194<br/>
          Private Bag X3015, Randburg 2125<br/>
          Tel: +27 11 709 4111<br/>
          www.mintek.co.za
        </div>
      </div>
      ${printContent}
      <div class="footer">
        MINTEK — Council for Mineral Technology · Generated by SiloGate Platform · ${new Date().toLocaleDateString('en-ZA', {day:'numeric',month:'long',year:'numeric'})} · CONFIDENTIAL
      </div>
    </body></html>`);
    
    printWindow.document.close();
    setTimeout(() => { printWindow.print(); }, 500);
  };

  return { printRef, triggerPrint };
}

// Wrapper component with ref forwarding
export const MintekPdfWrapper = React.forwardRef(({ title, subtitle, children }, ref) => (
  <div ref={ref} style={{ display: 'none' }}>
    <div className="report-title">
      <h1>{title}</h1>
      {subtitle && <p>{subtitle}</p>}
      <p>Generated: {new Date().toLocaleDateString('en-ZA', { day:'numeric', month:'long', year:'numeric', hour:'2-digit', minute:'2-digit' })}</p>
    </div>
    {children}
  </div>
));

// Helper: Generate stat card HTML for PDF
export function pdfStatCard(label, value) {
  return `<div class="stat-card"><div class="label">${label}</div><div class="value">${value}</div></div>`;
}

// Helper: Generate table HTML for PDF
export function pdfTable(headers, rows) {
  const ths = headers.map(h => `<th>${h}</th>`).join('');
  const trs = rows.map(r => `<tr>${r.map(c => `<td>${c ?? '—'}</td>`).join('')}</tr>`).join('');
  return `<table><thead><tr>${ths}</tr></thead><tbody>${trs}</tbody></table>`;
}

// Helper: Section title
export function pdfSection(title) {
  return `<div class="section-title">${title}</div>`;
}

// Helper: Badge
export function pdfBadge(text, type = 'blue') {
  return `<span class="badge badge-${type}">${text}</span>`;
}

// Helper: Progress bar
export function pdfProgress(percentage) {
  const color = percentage >= 70 ? '#10b981' : percentage >= 40 ? '#f59e0b' : '#ef4444';
  return `<div style="display:flex;align-items:center;gap:8px"><div class="progress-bar" style="flex:1"><div class="progress-fill" style="width:${percentage}%;background:${color}"></div></div><span style="font-weight:700;font-size:10px">${percentage}%</span></div>`;
}

/**
 * Quick PDF export function — pass HTML string directly
 * 
 * Usage:
 *   import { exportPdf } from '../utils/MintekPdfExport';
 *   exportPdf('APP Quarterly Report', 'Q1 FY2025/26', htmlContent);
 */
export function exportPdf(title, subtitle, htmlContent) {
  const win = window.open('', '_blank', 'width=900,height=700');
  if (!win) { alert('Please allow popups for PDF export'); return; }
  
  win.document.write(`<!DOCTYPE html><html><head><title>${title}</title>
    <style>
      @page { size: A4; margin: 15mm 20mm 20mm 20mm; }
      * { box-sizing: border-box; margin: 0; padding: 0; }
      body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; color: #1e293b; font-size: 11px; line-height: 1.5; }
      .letterhead { border-bottom: 3px solid ${MINTEK_RED}; padding-bottom: 12px; margin-bottom: 20px; display: flex; align-items: center; justify-content: space-between; }
      .letterhead-logo { display: flex; align-items: center; gap: 12px; }
      .letterhead-logo .logo-box { width: 48px; height: 48px; background: ${MINTEK_RED}; border-radius: 8px; display: flex; align-items: center; justify-content: center; color: white; font-weight: 900; font-size: 22px; }
      .letterhead-logo .org-name { font-size: 20px; font-weight: 800; color: ${MINTEK_RED}; letter-spacing: -0.5px; }
      .letterhead-logo .org-sub { font-size: 9px; color: #64748b; text-transform: uppercase; letter-spacing: 2px; }
      .letterhead-right { text-align: right; font-size: 8px; color: #94a3b8; line-height: 1.6; }
      .report-title { text-align: center; margin: 20px 0 24px; }
      .report-title h1 { font-size: 18px; font-weight: 800; color: #0f172a; }
      .report-title p { font-size: 10px; color: #64748b; margin-top: 4px; }
      .footer { position: fixed; bottom: 0; left: 0; right: 0; text-align: center; font-size: 7px; color: #94a3b8; border-top: 1px solid #e2e8f0; padding-top: 6px; }
      table { width: 100%; border-collapse: collapse; margin: 8px 0; font-size: 10px; }
      th { background: #f1f5f9; font-weight: 700; text-transform: uppercase; font-size: 8px; letter-spacing: 0.5px; color: #64748b; padding: 6px 8px; text-align: left; border-bottom: 2px solid #e2e8f0; }
      td { padding: 5px 8px; border-bottom: 1px solid #f1f5f9; }
      .stat-card { display: inline-block; padding: 10px 16px; border: 1px solid #e2e8f0; border-radius: 8px; margin: 4px; text-align: center; min-width: 100px; border-left: 3px solid ${MINTEK_RED}; }
      .stat-card .label { font-size: 8px; color: #64748b; text-transform: uppercase; font-weight: 600; }
      .stat-card .value { font-size: 18px; font-weight: 900; color: #0f172a; margin-top: 2px; }
      .section-title { font-size: 12px; font-weight: 700; color: ${MINTEK_RED}; text-transform: uppercase; letter-spacing: 0.5px; margin: 16px 0 8px; padding-bottom: 4px; border-bottom: 1px solid #fecaca; }
      .badge { display: inline-block; padding: 2px 6px; border-radius: 4px; font-size: 8px; font-weight: 600; }
      .badge-green { background: #dcfce7; color: #166534; }
      .badge-red { background: #fee2e2; color: #991b1b; }
      .badge-amber { background: #fef3c7; color: #92400e; }
      .badge-blue { background: #dbeafe; color: #1e40af; }
      .progress-bar { height: 6px; background: #f1f5f9; border-radius: 3px; overflow: hidden; display: inline-block; width: 80px; }
      .progress-fill { height: 100%; border-radius: 3px; }
      @media print { body { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; } }
    </style>
  </head><body>
    <div class="letterhead">
      <div class="letterhead-logo">
        <div class="logo-box">M</div>
        <div><div class="org-name">MINTEK</div><div class="org-sub">Council for Mineral Technology</div></div>
      </div>
      <div class="letterhead-right">200 Malibongwe Drive, Randburg 2194<br/>Private Bag X3015, Randburg 2125<br/>Tel: +27 11 709 4111<br/>www.mintek.co.za</div>
    </div>
    <div class="report-title"><h1>${title}</h1>${subtitle ? `<p>${subtitle}</p>` : ''}<p>Generated: ${new Date().toLocaleDateString('en-ZA', {day:'numeric',month:'long',year:'numeric'})}</p></div>
    ${htmlContent}
    <div class="footer">MINTEK — Council for Mineral Technology · Generated by SiloGate Platform · ${new Date().toLocaleDateString('en-ZA', {day:'numeric',month:'long',year:'numeric'})} · CONFIDENTIAL</div>
  </body></html>`);
  
  win.document.close();
  setTimeout(() => { win.print(); }, 500);
}

export default { useMintekPdf, MintekPdfWrapper, exportPdf, pdfStatCard, pdfTable, pdfSection, pdfBadge, pdfProgress };
